import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import {
  ENDPOINTS_PATH_NAME,
  ENDPOINT_DETECTIONS_MAX_ITEMS,
  ENDPOINT_RELATED_WEBPAGES_DEFAULT_LIMIT,
  ENDPOINT_RELATED_WEBPAGES_MAX_LIMIT,
} from './endpoints.constants';
import {
  EndpointsParamsSchema,
  EndpointsReturnSchema,
  EndpointsPreviewReturnSchema,
  EndpointsPreviewQuerySchema,
  CreateEndpointSchema,
  UpdateEndpointSchema,
  EndpointIdParamsSchema,
  EndpointReturnSchema,
  ErrorResponseSchema,
  DeleteResponseSchema,
  GenerateEndpointGraphResponseSchema,
  HarEntriesResponseSchema,
  HarEntryByIdParamsSchema,
  HarEntryResponseSchema,
  DeepActiveProbeResearchParamsSchema,
  MigrateEndpointsToVectorStoreQuerySchema,
  MigrateEndpointsToVectorStoreResponseSchema,
  TriggerEndpointSignalsGenerationResponseSchema,
  TriggerDeepActiveProbeResearchResponseSchema,
  EndpointExpectedResponseObservationsResponseSchema,
  ExpectedResponseObservationFilterOptionsSchema,
  ExpectedResponseObservationsQuerySchema,
  NeoRunThreadParamsSchema,
  EndpointDetectionsReturnSchema,
  EndpointWebpagesQuerySchema,
  EndpointWebpagesReturnSchema,
} from './endpoints.schema';
import {
  ReassociateHarEntriesBodySchema,
  ReassociateHarEntriesCollisionSchema,
  ReassociateHarEntriesReturnSchema,
} from './endpoints-reassociate.schema';
import { TriggerCodeGraphCatalogRunResponseSchema } from './endpoints-code-graph-catalog.schema';
import { z } from 'zod';

export const EndpointsContract = terraContract
  .prefix(`/${ENDPOINTS_PATH_NAME}`)
  .router({
    getAllEndpoints: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/',
        summary: 'List all endpoints',
        description:
          'Retrieves all API endpoints discovered across all applications. Endpoints represent individual API routes that have been identified through crawling, OpenAPI specs, or code analysis.',
        inputStructure: 'detailed',
      })
      .output(EndpointsReturnSchema),
    getEndpoints: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{applicationId}',
        summary: 'List endpoints by application',
        description:
          'Retrieves all API endpoints for a specific application with full details including request/response schemas, parameters, and authentication requirements.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: EndpointsParamsSchema }))
      .output(EndpointsReturnSchema),
    getEndpointsPreview: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{applicationId}/preview',
        summary: 'List endpoints preview',
        description:
          'Retrieves a lightweight preview of endpoints for list views. Supports filtering by URL, HTTP method, date ranges, and accessibility status. Use this for displaying endpoint lists without the overhead of full endpoint data.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: EndpointsParamsSchema,
          query: EndpointsPreviewQuerySchema,
        }),
      )
      .output(EndpointsPreviewReturnSchema),
    getEndpointById: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/id/{id}',
        summary: 'Get endpoint details',
        description:
          'Retrieves complete details for a single endpoint including its full request/response schema, discovered parameters, authentication method, and associated HAR entries.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: EndpointIdParamsSchema }))
      .output(EndpointReturnSchema)
      .errors({
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    getEndpointDetections: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/id/{id}/detections',
        summary: 'Get endpoint detections',
        description: `Retrieves findings and signals linked to a single endpoint for detection hover cards and detail views. Each collection is capped at ${ENDPOINT_DETECTIONS_MAX_ITEMS} items, newest first.`,
        inputStructure: 'detailed',
      })
      .input(z.object({ params: EndpointIdParamsSchema }))
      .output(EndpointDetectionsReturnSchema)
      .errors({
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    getEndpointWebpages: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/id/{id}/webpages',
        summary: 'Get webpages related to an endpoint',
        description: `Retrieves webpages from which this endpoint was observed during crawling or traffic capture. Defaults to ${ENDPOINT_RELATED_WEBPAGES_DEFAULT_LIMIT} items (max ${ENDPOINT_RELATED_WEBPAGES_MAX_LIMIT}).`,
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: EndpointIdParamsSchema,
          query: EndpointWebpagesQuerySchema,
        }),
      )
      .output(EndpointWebpagesReturnSchema)
      .errors({
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    getHarEntriesByEndpoint: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/id/{id}/har-entries',
        summary: 'Get HAR entries for endpoint',
        description:
          'Retrieves HTTP Archive (HAR) entries captured for a specific endpoint. HAR entries contain the actual HTTP requests and responses observed during crawling or traffic capture.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: EndpointIdParamsSchema,
          query: z.object({
            skip: z.coerce
              .number()
              .optional()
              .describe('Number of entries to skip for pagination'),
            limit: z.coerce
              .number()
              .optional()
              .describe('Maximum number of entries to return'),
          }),
        }),
      )
      .output(HarEntriesResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    getExpectedResponseObservationsByEndpoint: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/id/{id}/expected-response-observations',
        summary: 'Get expected response observations for endpoint',
        description:
          'Lists Neo attack HAR comparisons against code-derived expected responses for an endpoint.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: EndpointIdParamsSchema,
          query: ExpectedResponseObservationsQuerySchema.extend({
            threadId: z.string().optional(),
          }),
        }),
      )
      .output(EndpointExpectedResponseObservationsResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    getExpectedResponseObservationsByNeoRun: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{applicationId}/neo-runs/{threadId}/expected-response-observations',
        summary: 'Get expected response observations for Neo run',
        description:
          'Lists all expected response observations recorded during a Neo attack thread.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: NeoRunThreadParamsSchema,
          query: ExpectedResponseObservationsQuerySchema,
        }),
      )
      .output(EndpointExpectedResponseObservationsResponseSchema)
      .errors({
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    getExpectedResponseObservationFiltersByNeoRun: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{applicationId}/neo-runs/{threadId}/expected-response-observations/filters',
        summary:
          'Get filter options for Neo run expected response observations',
        description:
          'Returns distinct indexed filter values for expected response observations in a Neo run.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: NeoRunThreadParamsSchema }))
      .output(ExpectedResponseObservationFilterOptionsSchema)
      .errors({
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    getHarEntriesByApplication: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{applicationId}/har-entries',
        summary: 'Get HAR entries for application',
        description:
          'Retrieves all HAR entries captured across all endpoints in an application. Useful for traffic analysis and understanding API usage patterns.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: EndpointsParamsSchema,
          query: z.object({
            skip: z.coerce
              .number()
              .optional()
              .describe('Number of entries to skip for pagination'),
            limit: z.coerce
              .number()
              .optional()
              .describe('Maximum number of entries to return'),
          }),
        }),
      )
      .output(HarEntriesResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    getHarEntryById: terraContract
      .meta({
        openApi: true,
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{applicationId}/har-entries/{harEntryId}',
        summary: 'Get HAR entry by id',
        description:
          'Retrieves a single HAR entry including the full request and response bodies. Use the list endpoints for table rows, then this endpoint when opening, replaying, or adding an entry to chat.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: HarEntryByIdParamsSchema }))
      .output(HarEntryResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    createEndpoint: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/',
        summary: 'Create endpoint',
        description:
          'Manually creates a new endpoint entry. This is typically used when adding endpoints that were not automatically discovered, such as from internal documentation.',
        inputStructure: 'detailed',
        successStatus: 201,
      })
      .input(z.object({ body: CreateEndpointSchema }))
      .output(EndpointReturnSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    updateEndpoint: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PUT',
        path: '/{id}',
        summary: 'Update endpoint',
        description:
          'Updates endpoint properties such as authentication requirements, parameter schemas, or custom metadata.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: EndpointIdParamsSchema,
          body: UpdateEndpointSchema,
        }),
      )
      .output(EndpointReturnSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: ErrorResponseSchema },
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    deleteEndpoint: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{id}',
        summary: 'Delete endpoint',
        description:
          'Permanently deletes an endpoint and its associated data including HAR entries. This action cannot be undone.',
        inputStructure: 'detailed',
        successStatus: 204,
      })
      .input(z.object({ params: EndpointIdParamsSchema }))
      .output(DeleteResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    triggerEndpointGraphGeneration: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/{applicationId}/trigger-graph-generation',
        summary: 'Generate code graph',
        description:
          'Triggers code graph generation for a specific endpoint. This analyzes connected source code repositories to map the data flow from the endpoint to the backend implementation.',
        inputStructure: 'detailed',
        successStatus: 202,
      })
      .input(
        z.object({
          params: EndpointsParamsSchema,
          body: z.object({
            endpointId: z.string().describe('ID of the endpoint to analyze'),
          }),
        }),
      )
      .output(GenerateEndpointGraphResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    triggerDeepActiveProbeResearch: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/{applicationId}/id/{endpointId}/har-entries/{harEntryId}/trigger-deep-active-probe',
        summary: 'Trigger Deep active probe vector research for endpoint',
        description:
          'Publishes a Deep active probe research request for one endpoint. Deep active probe researches potential vulnerability vectors on it without exploiting them.',
        inputStructure: 'detailed',
        successStatus: 202,
      })
      .input(z.object({ params: DeepActiveProbeResearchParamsSchema }))
      .output(TriggerDeepActiveProbeResearchResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    triggerEndpointSignalsGeneration: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/id/{id}/trigger-signals-generation',
        summary: 'Trigger signals generation for endpoint',
        description:
          'Triggers signals generation for a specific endpoint by processing its HAR entry headers and cookies.',
        inputStructure: 'detailed',
        successStatus: 202,
      })
      .input(z.object({ params: EndpointIdParamsSchema }))
      .output(TriggerEndpointSignalsGenerationResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    triggerCodeGraphCatalogRun: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/id/{id}/code-graph/catalog-run',
        summary: 'Run catalog-scoped code graph generation for endpoint',
        description:
          'Regenerates the endpoint code graph using the repository catalog regardless of the catalog feature flag.',
        inputStructure: 'detailed',
        successStatus: 202,
      })
      .input(z.object({ params: EndpointIdParamsSchema }))
      .output(TriggerCodeGraphCatalogRunResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        CONFLICT: { status: 409, data: ErrorResponseSchema },
      }),
    migrateEndpointsToVectorStore: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/migrate-to-vector-store',
        summary: 'Migrate to vector store',
        description:
          'Migrates endpoint data to the vector store for semantic search capabilities. This is an administrative operation that indexes endpoints for AI-powered querying.',
        inputStructure: 'detailed',
      })
      .input(z.object({ query: MigrateEndpointsToVectorStoreQuerySchema }))
      .output(MigrateEndpointsToVectorStoreResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    reassociateHarEntries: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/id/{id}/har-entries/reassociate',
        summary: 'Reassociate HAR entries',
        description:
          'Moves selected HAR entry ids from this endpoint to an existing endpoint or a newly created one, and remounts signals whose harEntryId is in the moved set.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: EndpointIdParamsSchema,
          body: ReassociateHarEntriesBodySchema,
        }),
      )
      .output(ReassociateHarEntriesReturnSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: ErrorResponseSchema },
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        CONFLICT: { status: 409, data: ReassociateHarEntriesCollisionSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
  });
